import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { List, MusicNotes, CheckCircle, XCircle, Warning } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import api, { SPOTIFY_OAUTH_STATE_KEY } from '../services/api';
import Sidebar from '../components/Sidebar';
import Toast from '../components/Toast';
import {
  MUSIC_SERVICE_OPTIONS,
  setMusicService,
  useMusicService,
} from '../utils/musicService';

const MUSICKIT_SRC = 'https://js-cdn.music.apple.com/musickit/v3/musickit.js';

// Loads musickit.js on demand (only when this page needs it, per the
// milestone doc — never in index.html) and resolves once the
// `musickitloaded` event fires. Cached on window so repeat Connect clicks
// (or StrictMode double-invokes) don't inject the script twice -- but the
// cached promise is cleared on failure so a transient CDN/network blip
// doesn't permanently disable Connect for the rest of the page session;
// the next click appends a fresh <script> tag and retries.
function loadMusicKit() {
  if (window.MusicKit) return Promise.resolve(window.MusicKit);
  if (!window.__musicKitLoadPromise) {
    window.__musicKitLoadPromise = new Promise((resolve, reject) => {
      document.addEventListener('musickitloaded', () => resolve(window.MusicKit), { once: true });
      const script = document.createElement('script');
      script.src = MUSICKIT_SRC;
      script.onerror = () => reject(new Error('Failed to load MusicKit JS'));
      document.head.appendChild(script);
    }).catch((e) => {
      window.__musicKitLoadPromise = null;
      throw e;
    });
  }
  return window.__musicKitLoadPromise;
}

export default function Settings() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [connection, setConnection] = useState(null); // { connected, storefront } | null while loading
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [spotifyConnection, setSpotifyConnection] = useState(null); // { connected, spotify_user_id } | null while loading
  const [spotifyConnecting, setSpotifyConnecting] = useState(false);
  const [spotifyDisconnecting, setSpotifyDisconnecting] = useState(false);
  const [toast, setToast] = useState(null);
  const toastTimeoutRef = useRef(null);
  const musicService = useMusicService();

  const showToast = useCallback((message, type = 'info') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ message, type });
    toastTimeoutRef.current = setTimeout(() => setToast(null), 3000);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    api.getAppleMusicConnection()
      .then((status) => { if (!cancelled) setConnection(status); })
      .catch(() => {
        if (cancelled) return;
        // Fall back to a usable not-connected state rather than leaving
        // `connection` null forever -- null is also the loading sentinel,
        // so a stuck null permanently disables the Connect button behind
        // `loading` with no way to recover short of a full reload.
        setConnection({ connected: false, storefront: null });
        showToast('Failed to load Apple Music connection status', 'error');
      });
    return () => { cancelled = true; };
  }, [showToast]);

  useEffect(() => {
    let cancelled = false;
    api.getSpotifyConnection()
      .then((status) => { if (!cancelled) setSpotifyConnection(status); })
      .catch(() => {
        if (cancelled) return;
        setSpotifyConnection({ connected: false, spotify_user_id: null });
        showToast('Failed to load Spotify connection status', 'error');
      });
    return () => { cancelled = true; };
  }, [showToast]);

  // Read the router state SpotifyCallback.jsx hands back once, then clear it
  // so a refresh of /settings doesn't re-show the toast.
  useEffect(() => {
    if (location.state?.spotifyConnected) {
      showToast('Spotify connected', 'success');
      navigate(location.pathname, { replace: true, state: {} });
    } else if (location.state?.spotifyConnectFailed) {
      showToast("Spotify wasn't connected. Please try again.", 'error');
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const { developer_token: developerToken } = await api.getAppleDeveloperToken();
      const MusicKit = await loadMusicKit();
      const music = await MusicKit.configure({
        developerToken,
        app: { name: 'Collections', build: '1' },
      });
      // authorize() opens Apple's sign-in popup; the resolved value is the
      // Music User Token (MUT) — a live credential relayed to the backend
      // and never logged or stored client-side beyond this call.
      const musicUserToken = await music.authorize();
      const status = await api.connectAppleMusic(musicUserToken);
      setConnection(status);
      showToast('Apple Music connected', 'success');
    } catch (e) {
      // MusicKit doesn't cleanly distinguish "user closed the popup" from a
      // real failure (varies by version/browser), so this message is
      // deliberately neutral -- true whether authorize() was cancelled or
      // actually failed, instead of asserting an error that may not have
      // happened.
      showToast("Apple Music wasn't connected.", 'error');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      const status = await api.disconnectAppleMusic();
      setConnection(status);
      showToast('Apple Music disconnected', 'success');
    } catch (e) {
      showToast('Failed to disconnect Apple Music. Please try again.', 'error');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleConnectSpotify = async () => {
    setSpotifyConnecting(true);
    try {
      const state = crypto.randomUUID();
      sessionStorage.setItem(SPOTIFY_OAUTH_STATE_KEY, state);
      const { authorize_url: authorizeUrl } = await api.getSpotifyAuthorizeUrl(state);
      // Full-page redirect -- Spotify's authorize endpoint doesn't support
      // the MusicKit-style in-page popup flow Apple uses. No finally-based
      // setSpotifyConnecting(false) here since the page navigates away; only
      // clear the spinner if the call above fails before the redirect fires.
      window.location.href = authorizeUrl;
    } catch (e) {
      showToast("Spotify wasn't connected.", 'error');
      setSpotifyConnecting(false);
    }
  };

  const handleDisconnectSpotify = async () => {
    setSpotifyDisconnecting(true);
    try {
      const status = await api.disconnectSpotify();
      setSpotifyConnection(status);
      showToast('Spotify disconnected', 'success');
    } catch (e) {
      showToast('Failed to disconnect Spotify. Please try again.', 'error');
    } finally {
      setSpotifyDisconnecting(false);
    }
  };

  const loading = connection === null;
  const isConnected = !!connection?.connected;
  const spotifyLoading = spotifyConnection === null;
  const isSpotifyConnected = !!spotifyConnection?.connected;
  // COL-343: status endpoint detects a revoked grant and reports connected:
  // false, reauth_required: true instead of the stale connected: true it
  // used to return -- the row is still there server-side (never deleted),
  // this only changes what the card shows.
  const isSpotifyReauthRequired = !isSpotifyConnected && !!spotifyConnection?.reauth_required;

  return (
    <div className="app-layout">
      <Sidebar
        user={user}
        onLogout={logout}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <main className="main-content">
        <header className="content-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
            <button
              className="btn btn-ghost btn-icon sidebar-toggle"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open navigation"
            >
              <List size={20} />
            </button>
            <h1 className="page-title">Settings</h1>
          </div>
        </header>

        <div className="content-body">
          <section className="settings-section">
            <h2 className="settings-section-title">
              <MusicNotes size={18} />
              Primary music service
            </h2>

            <p className="settings-status-line">
              Decides which links music items show, and in what order. Display only —
              items are still analyzed and enriched for every service.
            </p>

            <div className="settings-choice-group" role="radiogroup" aria-label="Primary music service">
              {MUSIC_SERVICE_OPTIONS.map(({ value, label, description }) => (
                <label key={value} className="settings-choice">
                  <input
                    type="radio"
                    name="music-service"
                    value={value}
                    checked={musicService === value}
                    onChange={() => setMusicService(value)}
                  />
                  <span className="settings-choice-text">
                    <span className="settings-choice-label">{label}</span>
                    <span className="settings-choice-description">{description}</span>
                  </span>
                </label>
              ))}
            </div>
          </section>

          <section className="settings-section">
            <h2 className="settings-section-title">
              <MusicNotes size={18} />
              Apple Music
            </h2>

            {loading ? (
              <p className="settings-status-line">Checking connection…</p>
            ) : isConnected ? (
              <p className="settings-status-line settings-status-connected">
                <CheckCircle size={16} />
                Connected{connection.storefront ? ` (storefront: ${connection.storefront})` : ''}
              </p>
            ) : (
              <p className="settings-status-line settings-status-disconnected">
                <XCircle size={16} />
                Not connected
              </p>
            )}

            {isConnected ? (
              <button
                className="btn btn-ghost"
                onClick={handleDisconnect}
                disabled={disconnecting || loading}
              >
                {disconnecting ? 'Disconnecting…' : 'Disconnect'}
              </button>
            ) : (
              <button
                className="btn btn-primary"
                onClick={handleConnect}
                disabled={connecting || loading}
              >
                {connecting ? 'Connecting…' : 'Connect'}
              </button>
            )}
          </section>

          <section className="settings-section">
            <h2 className="settings-section-title">
              <MusicNotes size={18} />
              Spotify
            </h2>

            {spotifyLoading ? (
              <p className="settings-status-line">Checking connection…</p>
            ) : isSpotifyConnected ? (
              <p className="settings-status-line settings-status-connected">
                <CheckCircle size={16} />
                Connected{spotifyConnection.spotify_user_id ? ` (spotify_user_id: ${spotifyConnection.spotify_user_id})` : ''}
              </p>
            ) : isSpotifyReauthRequired ? (
              <p className="settings-status-line settings-status-disconnected">
                <Warning size={16} />
                Access was revoked. Reconnect to keep using Spotify.
              </p>
            ) : (
              <p className="settings-status-line settings-status-disconnected">
                <XCircle size={16} />
                Not connected
              </p>
            )}

            {isSpotifyConnected ? (
              <button
                className="btn btn-ghost"
                onClick={handleDisconnectSpotify}
                disabled={spotifyDisconnecting || spotifyLoading}
              >
                {spotifyDisconnecting ? 'Disconnecting…' : 'Disconnect'}
              </button>
            ) : (
              <button
                className="btn btn-primary"
                onClick={handleConnectSpotify}
                disabled={spotifyConnecting || spotifyLoading}
              >
                {spotifyConnecting
                  ? 'Connecting…'
                  : isSpotifyReauthRequired
                  ? 'Reconnect Spotify'
                  : 'Connect'}
              </button>
            )}
          </section>
        </div>
      </main>

      <Toast toast={toast} />
    </div>
  );
}
